import React, { useState } from 'react';
import { Button } from 'react-admin';
import {
  MenuItem,
  MenuList,
  Popover,
  Card,
  CardContent,
  ClickAwayListener,
  Grid,
  Collapse,
  ListItemText,
  TextField,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';

import SearchIcon from '@material-ui/icons/Search';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

const useListPopoverStyles = makeStyles({
  paper: {
    height: '340px',
    width: '400px',
    overflow: 'hidden'
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardContent: {
    'overflow-y': 'scroll',
    padding: '0 !important',
    margin: '16px',
    marginTop: '8px',
    border: '1px solid #E0E0E0',
    borderRadius: '2px',
    flex: '1'
  },
  inputSearchContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '30px',
    margin: '16px 16px 8px 16px',
    borderRadius: '2px',
    alignItems: 'center'
  },
  inputSearch: {
    backgroundColor: '#FFFFFF !important',
    borderRadius: '2px',
    borderTop: '1px solid #E0E0E0',
    borderLeft: '1px solid #E0E0E0',
    borderRight: '1px solid #E0E0E0'
  },
  input: {
    paddingTop: '5px',
    paddingBottom: '6px'
  },
  listItem: {
    margin: '0px'
  },
  button: {
    paddingTop: '0px !important',
    paddingRight: '0px',
    '& span': {
      paddingRight: '0px !important'
    }
  }
});

/**
 * Popover with list of select items and search
 */
const ListPopover = props => {
  const {
    classes: classesOverride,
    searchPlaceholder,
    onSelectItem,
    items: allItems,
    buttonLabel,
    source
  } = props;

  const classes = useListPopoverStyles({ classes: classesOverride });
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(allItems);
  const [currentOpenItem, setCurrentOpenItem] = useState(null);

  const openPopover = Boolean(anchorEl);

  function handleClickItem(item) {
    if (item.values) {
      if (currentOpenItem === item.value) {
        setCurrentOpenItem(null);
      } else {
        setCurrentOpenItem(item.value);
      }
    } else {
      onSelectItem(item);
      setAnchorEl(null);
      setCurrentOpenItem(null);
    }
  }

  function handleChangeSearch(value) {
    setSearch(value);
    setItems(
      allItems.filter(item => {
        return item.label.toLowerCase().includes(value.toLowerCase());
      })
    );
  }

  /**
   * Reset search
   */
  function handleExited() {
    if (search.length > 0) {
      setSearch('');
      setItems(allItems);
    }
  }

  return (
    <Grid>
      <Button
        label={buttonLabel || `Insert ${capitalize(source)} Variable`}
        variant="text"
        onClick={evt => setAnchorEl(evt.currentTarget)}
        classes={{ button: classes.button }}
      />
      <Popover
        open={openPopover}
        TransitionProps={{ onExited: handleExited }}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'center' }}
        transformOrigin={{ horizontal: 'right', vertical: 'center' }}
        PaperProps={{
          classes: { root: classes.paper }
        }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Card classes={{ root: classes.card }}>
            <div className={classes.inputSearchContainer}>
              <TextField
                variant="filled"
                // margin="none"
                hiddenLabel
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  classes: {
                    adornedStart: classes.inputSearch,
                    input: classes.input
                  }
                }}
                onChange={evt => handleChangeSearch(evt.target.value)}
                value={search}
                inputProps={{
                  placeholder: searchPlaceholder
                }}
              />
            </div>
            <CardContent classes={{ root: classes.cardContent }}>
              <MenuList disablePadding>
                {items.map((item, index) => (
                  <div key={index}>
                    <MenuItem
                      onClick={() => handleClickItem(item)}
                      data-testid="sub-menu"
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ variant: 'body2' }}
                        classes={{ root: classes.listItem }}
                      />
                      {item.values &&
                        (currentOpenItem === item.value ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        ))}
                    </MenuItem>
                    {item.values && item.values.length > 0 && (
                      <Collapse in={currentOpenItem === item.value}>
                        <MenuList>
                          {item.values.map((subItem, subIndex) => (
                            <MenuItem
                              key={subIndex}
                              onClick={() => handleClickItem(subItem)}
                              dense={false}
                            >
                              <ListItemText
                                primary={subItem.label}
                                primaryTypographyProps={{ variant: 'body2' }}
                                data-testid="sub-item"
                                inset
                                classes={{ root: classes.listItem }}
                              />
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Collapse>
                    )}
                  </div>
                ))}
              </MenuList>
            </CardContent>
          </Card>
        </ClickAwayListener>
      </Popover>
    </Grid>
  );
};

ListPopover.defaultProps = {
  searchPlaceholder: 'Search...'
};

ListPopover.propTypes = {
  searchPlaceholder: PropTypes.string,
  buttonLabel: PropTypes.string,
  onSelectItem: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string
        })
      )
    })
  ).isRequired
};

export default ListPopover;
